import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import useAuth from '@/hooks/useAuth'
import EmailInput from '@components/auth/EmailInput/EmailInput'
import PasswordInput from '@components/auth/PasswordInput/PasswordInput'
import Button from '@components/common/Button/Button'
import Checkbox from '@components/common/Checkbox/Checkbox'
import Logo from '@components/common/Logo/Logo'

import BoltWhiteLogo from '@assets/logos/bolt-white.png'

import MotionText from '@/components/common/Motion/MotionText'
import { useAttributionStore } from '@/store/useAttributionStore'
import { usePageSetup } from '@/hooks/usePageSetup'

export const UserRegisterPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('auth.pageTitle.register'), { scroll: true, noindex: true })

  const navigate = useNavigate()

  const { clearAttribution, ...attribution } = useAttributionStore()
  const { register, isLoading, error } = useAuth()

  const isBoltAttribution = attribution.utm_source === 'bolt'

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)

    try {
      await register({
        user: {
          email: formData.get('email') as string,
          password: formData.get('password') as string,
          marketing_consent: formData.get('marketing_consent') === 'on',
          terms_accepted: formData.get('terms_accepted') === 'on',
          attribution_attributes: {
            utm_source: attribution.utm_source,
            utm_medium: attribution.utm_medium,
            utm_campaign: attribution.utm_campaign,
            utm_term: attribution.utm_term,
            referrer: attribution.referrer,
          },
        }
      })

      clearAttribution()
      navigate("/kyc/intro")
    } catch (error: any) { }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="h-full bg-white p-5 pt-8 pb-8 flex flex-col">
      {isBoltAttribution && (
        <div className="h-12 bg-[#2a9c64] absolute top-0 left-0 w-full flex flex-row items-center justify-center">
          <h1 className="mt-1 font-bold text-lg text-white">
            {t('auth.platformSponsor')}
          </h1>
          <img src={BoltWhiteLogo} className="h-full w-auto" />
        </div>
      )}
      <div
        onClick={() => navigate('/')}
        className={`space-y-4 ${isBoltAttribution ? 'pt-9' : ''}`}>
        <Logo />
      </div>

      <div className="flex-1 flex flex-col">
        <div className='h-[2rem] w-1'></div>
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('auth.createAccount')}
          </MotionText>
        </div>
        <motion.div className='mt-6 flex flex-col gap-4'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
        >
          <EmailInput
            name='email'
            required
            autoComplete='email'
            label={t('auth.yourEmail')}
          />
          <PasswordInput
            error={error?.message}
            name='password'
            required
            autoComplete="current-password"
            label={t('auth.createPassword')}
          />
        </motion.div>
        <Checkbox
          name='marketing_consent'
          className='mt-6 mb-2'
          label={t('auth.marketingConsent')}
        />
        <Checkbox
          name='terms_accepted'
          required
          label={
            <span>
              {t('auth.termsAcceptance')}{' '}
              <Link className="text-blue-600" to='/legal/cookie-policy' target='_blank'>
                {t('common.cookiePolicy')}
              </Link>
              ,{' '}
              <Link className="text-blue-600" to='/legal/privacy-policy' target='_blank'>
                {t('common.privacyPolicy')}
              </Link>
              {' '}{t('auth.and')}{' '}
              <Link className="text-blue-600" to='/legal/terms-of-service' target='_blank'>
                {t('common.termsOfService')}
              </Link>
              . <span className='text-red-500'>*</span>
            </span>
          }
        />
      </div>
      <p className="text-xs text-zinc-500 text-center mt-8 mb-4">
        {isBoltAttribution
          ? t('auth.pricingBoltFree')
          : t('auth.pricing')
        }
      </p>
      <div className="flex flex-row gap-4">
        <Button type='submit' isLoading={isLoading}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
          >
            {t('common.continue')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default UserRegisterPage